import { useEffect, useState } from "react";

import { useScrollLock } from "../lib/useScrollLock";

/**
 * 统一确认弹窗（卡片样式，呼应登录卡片）。
 * 用 open 控制显隐；danger 时主按钮用警示色。
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "确认",
  cancelLabel = "取消",
  danger = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") { e.preventDefault(); onConfirm(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel, onConfirm]);
  useScrollLock(open);

  if (!open) return null;
  return (
    <div className="amodal-backdrop" onClick={onCancel}>
      <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirm-title">{title}</h3>
        {message && <div className="confirm-msg">{message}</div>}
        <div className="confirm-actions">
          <button className="btn" onClick={onCancel}>{cancelLabel}</button>
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm} autoFocus>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 提示弹窗（单按钮，卡片样式同 ConfirmDialog）。ESC / Enter / 按钮均关闭。
 * 用于「操作失败，告知原因」这类只需确认知晓的场景。
 */
export function AlertDialog({
  open, title, message, confirmLabel = "知道了", danger = false, onClose,
}: {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") { e.preventDefault(); onClose(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  useScrollLock(open);

  if (!open) return null;
  return (
    <div className="amodal-backdrop" onClick={onClose}>
      <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirm-title">{title}</h3>
        {message && <div className="confirm-msg">{message}</div>}
        <div className="confirm-actions">
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onClose} autoFocus>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 文本输入弹窗（卡片样式，同 ConfirmDialog）。ESC 取消、Enter 确认。
 * 用 open 控制显隐；initialValue 初始填入并全选。
 */
export function PromptDialog({
  open, title, label, initialValue = "", confirmLabel = "确认", cancelLabel = "取消", onConfirm, onCancel,
}: {
  open: boolean;
  title: string;
  label?: string;
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => { if (open) setValue(initialValue); }, [open, initialValue]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);
  useScrollLock(open);

  if (!open) return null;
  const submit = () => { const v = value.trim(); if (v) onConfirm(v); };
  return (
    <div className="amodal-backdrop" onClick={onCancel}>
      <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirm-title">{title}</h3>
        {label && <div className="confirm-msg">{label}</div>}
        <input
          value={value}
          autoFocus
          onFocus={(e) => e.target.select()}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          style={{ width: "100%", marginTop: 4 }}
        />
        <div className="confirm-actions" style={{ marginTop: 16 }}>
          <button className="btn" onClick={onCancel}>{cancelLabel}</button>
          <button className="btn btn-primary" onClick={submit}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
